import {Input, message, Modal, Switch} from "antd";
import React, {useState, useEffect} from "react";
import GoogleMapReact from "google-map-react";
import {EnvironmentFilled, SearchOutlined} from "@ant-design/icons";
import {CityFilter} from "../../../_defaultComponents/FilterComponents";
import DistrictFilter from "../../../_defaultComponents/FilterComponents/DistrictFilter";
import TextArea from "antd/lib/input/TextArea";
import apiKey from "../../../../_core/apiKey";
import styled from "styled-components";
import {useFetch} from "../../../../hooks/useFetch";
import {useStore} from "../../../../hooks/useStore";

const Marker = () => <EnvironmentFilled style={{fontSize: "24px", color: "#1890ff"}} />;

const SuggestionsContainer = styled.ul`
    list-style: none;
    padding: 0;
    margin-top: 4px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    background: white;
`;

const SuggestionItem = styled.li`
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:hover {
        background-color: #f5f5f5;
    }
`;

const AddressContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const TransferToNextDayModal = ({
    setTransferToNextDayModalVisible,
    transferToNextDayModalVisible,
    shipmentId,
    undeliveredShipmentId,
    undeliveredShipmentsRefetch
}) => {
    const [city, setCity] = useState(null);
    const [district, setDistrict] = useState(null);
    const [addressChange, setAddressChange] = useState(false);
    const [address, setAddress] = useState("");
    const [addressDetail, setAddressDetail] = useState("");
    const [position, setPosition] = useState({
        lat: 41.0082,
        lng: 28.9784
    });
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const stores = useStore();
    const [errors, setErrors] = useState({
        city: false,
        district: false
    });

    const [scheduleNextDayLoading, scheduleNextDay, , , refetch] = useFetch({
        fetchFn: () =>
            stores.transferStore.scheduleNextDay({
                shipmentId,
                undeliveredShipmentId,
                addressUpdate: addressChange
                    ? {
                          city,
                          district,
                          address: address + " " + addressDetail,
                          lat: position?.lat,
                          lon: position?.lng
                      }
                    : undefined
            }),
        initialCall: false
    });

    const mapStyles = {
        height: "400px",
        width: "100%",
        marginTop: "20px"
    };

    const handleAddressChange = value => {
        setAddress(value);
        if (value && value.length > 2) {
            const geocoder = new window.google.maps.Geocoder();
            const fullAddress = `${value}, ${district || ""}, ${city || ""}, Türkiye`;

            geocoder.geocode({address: fullAddress}, (results, status) => {
                if (status === "OK" && results) {
                    setAddressSuggestions(
                        results.map(result => ({
                            address: result.formatted_address,
                            location: {
                                lat: result.geometry.location.lat(),
                                lng: result.geometry.location.lng()
                            }
                        }))
                    );
                }
            });
        } else {
            setAddressSuggestions([]);
        }
    };

    const handleAddressSelect = suggestion => {
        setAddress(suggestion.address);
        setPosition(suggestion.location);
        setAddressSuggestions([]);
    };

    const handleMapClick = ({lat, lng}) => {
        setPosition({lat, lng});

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({location: {lat, lng}}, (results, status) => {
            if (status === "OK" && results[0]) {
                setAddress(results[0].formatted_address);
            }
        });
    };

    const handleCityChange = value => {
        setCity(value);
        setAddress("");
        setAddressDetail("");
        setAddressSuggestions([]);
    };

    const handleDistrictChange = value => {
        setDistrict(value);
        setAddress("");
        setAddressDetail("");
        setAddressSuggestions([]);
    };

    const validateForm = () => {
        if (!addressChange) return true;

        const newErrors = {
            city: !city,
            district: !district
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const handleScheduleNextDay = async () => {
        try {
            if (validateForm()) {
                await refetch();
                undeliveredShipmentsRefetch();
                setTransferToNextDayModalVisible(false);
            } else {
                message.error("Lütfen İl ve İlçe alanlarını doldurun!");
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (city && district) {
            const geocoder = new window.google.maps.Geocoder();
            const locationQuery = `${district}, ${city}, Türkiye`;

            geocoder.geocode({address: locationQuery}, (results, status) => {
                if (status === "OK" && results[0]) {
                    setPosition({
                        lat: results[0].geometry.location.lat(),
                        lng: results[0].geometry.location.lng()
                    });
                }
            });
        }
    }, [city, district]);

    useEffect(() => {
        if (transferToNextDayModalVisible) {
            setAddressChange(false);
            setCity(null);
            setDistrict(null);
            setAddress("");
            setAddressDetail("");
            setPosition({lat: 41.0082, lng: 28.9784});
            setErrors({city: false, district: false});
            setAddressSuggestions([]);
        }
    }, [transferToNextDayModalVisible]);

    return (
        <Modal
            visible={transferToNextDayModalVisible}
            onCancel={() => {
                setTransferToNextDayModalVisible(false);
            }}
            onOk={handleScheduleNextDay}
            confirmLoading={scheduleNextDayLoading}
            okText="Sonraki Güne Aktar"
            cancelText="İptal"
        >
            <div style={{marginBottom: "20px"}}>
                <span style={{marginRight: "10px"}}>Adres değişikliği yapılsın mı?</span>
                <Switch
                    checked={addressChange}
                    checkedChildren="Evet"
                    unCheckedChildren="Hayır"
                    onChange={checked => {
                        setAddressChange(checked);
                    }}
                />
            </div>
            {addressChange && (
                <div>
                    <h3>Yeni Adres Bilgileri</h3>
                    <CityFilter
                        setCity={setCity}
                        city={city}
                        onChange={handleCityChange}
                        status={errors.city ? "error" : ""}
                    />
                    <DistrictFilter
                        city={city}
                        setDistrict={setDistrict}
                        district={district}
                        onChange={handleDistrictChange}
                        status={errors.district ? "error" : ""}
                    />
                    <AddressContainer>
                        <div>
                            <h5>Adres Araması</h5>
                            <Input
                                placeholder="Adres araması yapın"
                                value={address}
                                onChange={e => handleAddressChange(e.target.value)}
                                prefix={<SearchOutlined style={{color: "#bfbfbf"}} />}
                            />
                            {addressSuggestions.length > 0 && (
                                <SuggestionsContainer>
                                    {addressSuggestions.map((suggestion, index) => (
                                        <SuggestionItem
                                            key={index}
                                            onClick={() => handleAddressSelect(suggestion)}
                                        >
                                            <EnvironmentFilled
                                                style={{marginRight: 8, color: "#1890ff"}}
                                            />
                                            {suggestion.address}
                                        </SuggestionItem>
                                    ))}
                                </SuggestionsContainer>
                            )}
                        </div>

                        <div>
                            <h5>Adres Detayı</h5>
                            <TextArea
                                placeholder="Daire no, kat, bina adı vb. detayları yazın"
                                autoSize={{minRows: 2, maxRows: 4}}
                                value={addressDetail}
                                onChange={e => setAddressDetail(e.target.value)}
                            />
                        </div>
                    </AddressContainer>
                    <div style={mapStyles}>
                        <GoogleMapReact
                            bootstrapURLKeys={{
                                key: apiKey,
                                libraries: ["places"],
                                language: "tr"
                            }}
                            defaultCenter={position}
                            center={position}
                            defaultZoom={13}
                            onClick={handleMapClick}
                        >
                            <Marker lat={position.lat} lng={position.lng} />
                        </GoogleMapReact>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default TransferToNextDayModal;
